/**
 * The example configs — the one source of them: `acp show-style-template` and
 * `acp studio-template` print them, the SPA's "create from template" offers
 * them, and the dev tools and gates seed from them. D32 splits the example in
 * two:
 *
 *   showStyleTemplate()              the show style (`showStyle.ts`): layers `vmx_pgm`,
 *                                    `vmx_dsk`, `vs_a`, `vs_audio`, `gfx_lt`, `gfx_full`; source
 *                                    and output layers; the piece types camera, vt, audio,
 *                                    lower-third and fullscreen; a break action; a panel
 *   studioTemplate(host, basePort)   the studio (`studio.ts`): the three simulated devices
 *                                    (PLAN §7.3) — a vision mixer `vmx1`, a video server `vs1`,
 *                                    graphics `gfx1` — and its mappings of those layers
 *   productionTemplate(host, basePort)  the two resolved (`resolveConfig.ts`): the production
 *                                    config the engine runs, for tests and tools that drive an
 *                                    engine or a device host directly
 *
 * Device types, mapping options (0-based `me`/`keyer`/`aux`, `channel`,
 * `layer`; `oneShot` true for video-server channels) and the timeline
 * content's `deviceType` are the simulated devices' names (wave 3a-B). Layer
 * ids are timeline layers, so `[A-Za-z0-9_]` only; they keep their pre-D32
 * names, so a studio split by the migration and one made from these templates
 * are alike. The simulators listen on the checkout's port block
 * (`dev/sessionPorts.ts`: base + 5, 6, 7); `--base-port` names the block.
 * Pure data, no I/O: it lives in the engine beside the schemas it must
 * satisfy (`studioTemplate.test.ts`).
 */
import { jsonValueSchema, type JsonValue } from '@norna/domain/primitives.js';
import { z } from 'zod';
import { parseProductionConfig, type ProductionConfig } from './productionConfig.js';
import { resolveProductionConfig } from './resolveConfig.js';
import { parseShowStyleConfig } from './showStyle.js';
import { parseStudioConfig } from './studio.js';
import { SIM_PORT_OFFSETS } from './simPorts.js';

type JsonObject = { [key: string]: JsonValue };

export function showStyleTemplate(): JsonObject {
  // The simulated devices' timeline content (packages/devices/src/sim): a mixer M/E cut, a clip, a template.
  const mixer = { deviceType: 'ACP_SIM_MIXER', type: 'me' };
  const video = { deviceType: 'ACP_SIM_VIDEO', type: 'clip' };
  const graphics = { deviceType: 'ACP_SIM_GRAPHICS', type: 'template' };
  const text = (label: string, required: boolean) => ({ type: 'string', label, required });
  return {
    layers: {
      vmx_pgm: { label: 'Mixer programme', deviceTypes: ['sim-vision-mixer'] },
      vmx_dsk: { label: 'Mixer downstream key', deviceTypes: ['sim-vision-mixer'] },
      vs_a: { label: 'Clip channel A', deviceTypes: ['sim-video-server'] },
      vs_audio: { label: 'Audio bed', deviceTypes: ['sim-video-server'] },
      gfx_lt: { label: 'Graphics: lower third', deviceTypes: ['sim-graphics'] },
      gfx_full: { label: 'Graphics: fullscreen', deviceTypes: ['sim-graphics'] },
    },
    sourceLayers: {
      cam: { label: 'Camera' },
      vt: { label: 'VT' },
      audio: { label: 'Audio' },
      lt: { label: 'Lower third' },
      full: { label: 'Fullscreen graphics' },
    },
    outputLayers: {
      pgm: { label: 'Programme', isPgm: true },
      overlay: { label: 'Overlay', isPgm: false },
    },
    pieceTypes: {
      camera: {
        label: 'Camera',
        params: { input: { type: 'integer', label: 'Mixer input', required: true, minimum: 1, maximum: 8 } },
        sourceLayer: 'cam',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [{ layer: 'vmx_pgm', content: { ...mixer, input: '{{input}}' } }],
      },
      vt: {
        label: 'VT',
        params: { clip: text('Clip', true) },
        sourceLayer: 'vt',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        prerollDuration: 200,
        timeline: [
          { layer: 'vs_a', content: { ...video, clip: '{{clip}}' } },
          { layer: 'vmx_pgm', content: { ...mixer, input: 3 } },
        ],
      },
      audio: {
        label: 'Audio',
        params: { file: text('File', true) },
        sourceLayer: 'audio',
        outputLayer: 'pgm',
        lifespan: 'segment-end',
        defaultDuration: null,
        timeline: [{ layer: 'vs_audio', content: { ...video, clip: '{{file}}', loop: true } }],
      },
      'lower-third': {
        label: 'Lower third',
        params: { name: text('Name', true), title: text('Title', false) },
        sourceLayer: 'lt',
        outputLayer: 'overlay',
        lifespan: 'part-only',
        defaultDuration: 6000,
        timeline: [{ layer: 'gfx_lt', content: { ...graphics, template: 'lower-third', data: { name: '{{name}}', title: '{{title}}' } } }],
      },
      fullscreen: {
        label: 'Fullscreen',
        params: { template: text('Template', true) },
        sourceLayer: 'full',
        outputLayer: 'pgm',
        lifespan: 'part-only',
        defaultDuration: null,
        timeline: [
          { layer: 'gfx_full', content: { ...graphics, template: '{{template}}', data: {} } },
          { layer: 'vmx_pgm', content: { ...mixer, input: 4 } },
        ],
      },
    },
    actions: {
      break: { label: 'Break', steps: [{ stop: 'lt' }, { stop: 'full' }, { pieceType: 'vt', params: { clip: 'break' }, mode: 'now' }] },
    },
    adlibPanels: [
      {
        id: 'main',
        label: 'Main',
        items: [
          { label: 'Cam 1', pieceType: 'camera', params: { input: 1 }, hotkey: 'F1' },
          { label: 'Cam 2', pieceType: 'camera', params: { input: 2 }, hotkey: 'F2' },
          { label: 'Lower third', pieceType: 'lower-third', params: { name: 'Name', title: 'Title' } },
          { label: 'Bumper', pieceType: 'vt', params: { clip: 'bumper' }, mode: 'queue' },
          { label: 'Break', action: 'break' },
        ],
      },
    ],
  };
}

export function studioTemplate(host: string, basePort: number): JsonObject {
  return {
    devices: {
      vmx1: { type: 'sim-vision-mixer', standbyPolicy: 'observe', options: { host, port: basePort + SIM_PORT_OFFSETS.mixer } },
      vs1: { type: 'sim-video-server', standbyPolicy: 'observe', options: { host, port: basePort + SIM_PORT_OFFSETS.video } },
      gfx1: { type: 'sim-graphics', standbyPolicy: 'observe', options: { host, port: basePort + SIM_PORT_OFFSETS.graphics } },
    },
    mappings: {
      vmx_pgm: { device: 'vmx1', lookahead: 'preload', oneShot: false, options: { mappingType: 'me', me: 0 } },
      vmx_dsk: { device: 'vmx1', lookahead: 'none', oneShot: false, options: { mappingType: 'keyer', me: 0, keyer: 0 } },
      vs_a: { device: 'vs1', lookahead: 'preload', oneShot: true, options: { mappingType: 'channel', channel: 0 } },
      vs_audio: { device: 'vs1', lookahead: 'none', oneShot: true, options: { mappingType: 'channel', channel: 1 } },
      gfx_lt: { device: 'gfx1', lookahead: 'none', oneShot: false, options: { mappingType: 'layer', layer: 0 } },
      gfx_full: { device: 'gfx1', lookahead: 'none', oneShot: false, options: { mappingType: 'layer', layer: 1 } },
    },
    settings: { minimumTakeSpanMs: 1000 },
  };
}

/** The two templates resolved: the production config (pre-D32 shape) the engine runs. Throws only if the templates themselves are broken. */
export function resolvedTemplate(host: string, basePort: number): ProductionConfig {
  const showStyle = parseShowStyleConfig(showStyleTemplate());
  const studio = parseStudioConfig(studioTemplate(host, basePort));
  if (!showStyle.ok) throw new Error(`the show-style template is invalid: ${showStyle.error}`);
  if (!studio.ok) throw new Error(`the studio template is invalid: ${studio.error}`);
  const resolved = resolveProductionConfig(showStyle.config, studio.config);
  if (!resolved.ok) throw new Error(`the templates do not resolve: ${JSON.stringify(resolved.problems)}`);
  return resolved.config;
}

/**
 * The resolved templates as JSON input — what a pre-D32 studio config looked
 * like, and what a production's `config` holds. Round-trips through
 * `parseProductionConfig` to itself.
 */
export function productionTemplate(host: string, basePort: number): JsonObject {
  const config = resolvedTemplate(host, basePort);
  const reparsed = parseProductionConfig(config);
  if (!reparsed.ok) throw new Error(`the resolved template does not re-parse: ${reparsed.error}`);
  return toJson(reparsed.config);
}

function toJson(config: ProductionConfig): JsonObject {
  // A parsed config is plain JSON data (zod output of JSON input); the round trip drops nothing.
  return z.record(z.string(), jsonValueSchema).parse(JSON.parse(JSON.stringify(config)));
}
